import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@/components/ui';
import { DataTable, Tabs, TabList, TabTrigger, TabContent, PageHeader, PageLoading, ErrorBanner } from '@/components/ui';
import type { IRepository } from '@skystone/shared';
import { repositoryApi } from '@/services/repository-api';
import { useApi } from '@/hooks/useApi';
import { renderArchivedEntityCell, renderFabricSyncedCell, renderOptionalStringCell } from '@/lib/table-cell-renderers';
import { PageContainer } from '@/components/layout';

type ViewMode = 'connections' | 'list';

const columns: ColumnDef<IRepository>[] = [
  {
    id: 'name',
    header: 'Repository',
    accessor: 'name',
    sortable: true,
    filter: { type: 'text', placeholder: 'Filter repository...' },
    cell: (_value, row) => (
      <Link to={`/admin/repositories/${encodeURIComponent(row.id)}`} className="text-primary hover:underline font-medium">
        {row.name}
      </Link>
    ),
  },
  {
    id: 'products',
    header: 'Products',
    accessor: (row: IRepository) => row.products.map((p) => p.name).join(', ') || 'Unassigned',
    sortable: true,
    filter: { type: 'text', placeholder: 'Filter product...' },
  },
  {
    id: 'fabricRepositoryId',
    header: 'Fabric Repository ID',
    accessor: 'fabricRepositoryId',
    cell: renderOptionalStringCell,
  },
  {
    id: 'fabricSynced',
    header: 'Fabric',
    accessor: 'fabricSynced',
    cell: renderFabricSyncedCell,
  },
  {
    id: 'archived',
    header: 'Status',
    accessor: 'archived',
    cell: renderArchivedEntityCell,
  },
];

export function RepositoryConnectionsPage() {
  const [searchParams] = useSearchParams();
  const repositoryIdFilter = searchParams.get('repositoryId')?.trim() || '';
  const [showArchived, setShowArchived] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('connections');

  const {
    data: connections,
    loading: loadingConnections,
    error: connectionsError,
  } = useApi(() => repositoryApi.getConnections(showArchived), [showArchived]);

  const {
    data: repositories,
    loading: loadingRepositories,
    error: repositoriesError,
  } = useApi(
    () => repositoryApi.getAll({ includeArchived: showArchived, includeUnassigned: true }),
    [showArchived],
  );

  const connectionsRows = useMemo<IRepository[]>(() => {
    if (!connections) return [];
    return [
      ...connections.products.flatMap((product) =>
        product.repositories.map((repository) => ({
          ...repository,
          products: [{ id: product.id, name: product.name }],
        })),
      ),
      ...connections.unassignedRepositories,
    ];
  }, [connections]);

  const filteredConnectionsRows = useMemo<IRepository[]>(() => {
    if (!repositoryIdFilter) {
      return connectionsRows;
    }
    return connectionsRows.filter((repository) => repository.id === repositoryIdFilter);
  }, [connectionsRows, repositoryIdFilter]);

  const filteredRepositories = useMemo<IRepository[]>(() => {
    const allRepositories = repositories ?? [];
    if (!repositoryIdFilter) {
      return allRepositories;
    }
    return allRepositories.filter((repository) => repository.id === repositoryIdFilter);
  }, [repositories, repositoryIdFilter]);

  const loading = loadingConnections || loadingRepositories;
  const error = connectionsError ?? repositoriesError;

  if (loading && !connections && !repositories) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Repository Connections"
        subtitle={
          repositoryIdFilter
            ? 'Showing connection details for selected repository from Org Hierarchy.'
            : 'Review Fabric-synced repository-to-product links in connection and list views.'
        }
      />

      <ErrorBanner message={error} />

      <div className="flex items-center gap-2">
        <label
          htmlFor="show-archived-repositories"
          className="text-sm text-muted-foreground cursor-pointer select-none"
        >
          Show archived
        </label>
        <input
          id="show-archived-repositories"
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
        />
      </div>

      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabList>
          <TabTrigger value="connections">Connections</TabTrigger>
          <TabTrigger value="list">Flat List</TabTrigger>
        </TabList>

        <TabContent value="connections">
          <DataTable
            data={filteredConnectionsRows}
            columns={columns}
            keyExtractor={(repository) => repository.id}
            enablePagination
            enableFilters
            emptyMessage="No repository connections found."
          />
        </TabContent>

        <TabContent value="list">
          <DataTable
            data={filteredRepositories}
            columns={columns}
            keyExtractor={(repository) => repository.id}
            enablePagination
            enableFilters
            emptyMessage="No repositories found."
          />
        </TabContent>
      </Tabs>
    </PageContainer>
  );
}
